import { useState, useCallback } from 'react';
import { useRealtimeRisk } from '../hooks/useRealtimeRisk';
import { useMapLayer } from '../hooks/useMapLayer';
import { PORTUGAL_BOUNDS } from '../types';
import MapShell from '../components/map/MapShell';
import MapClickHandler from '../components/map/MapClickHandler';
import RiskMarker from '../components/map/RiskMarker';
import RiskLegend from '../components/map/RiskLegend';
import CellPopup from '../components/map/CellPopup';
import LayerControl from '../components/map/LayerControl';
import ModuleBadge from '../components/layout/ModuleBadge';
import Spinner from '../components/ui/Spinner';
import Alert from '../components/ui/Alert';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const PANEL = 'rounded-xl border border-ink-700 bg-ink-900/85 shadow-2xl backdrop-blur';

export default function RiskMap() {
  useDocumentMeta(
    'Consultar o risco de incêndio num ponto | Tutela IGNIS',
    'Clique em qualquer ponto de Portugal e veja a previsão de risco de incêndio em tempo real para essa localização exata.',
    '/ignis/map',
  );
  const { layer: mapLayer, setLayer: setMapLayer, config: layerConfig } = useMapLayer();
  const [clickedPoint, setClickedPoint] = useState<{ lat: number; lon: number } | null>(null);

  // Model is locked to the active production model (no public model selector).
  const { data: prediction, isLoading, error } = useRealtimeRisk(clickedPoint, undefined);

  const handleMapClick = useCallback((lat: number, lon: number) => {
    if (
      lat >= PORTUGAL_BOUNDS.south &&
      lat <= PORTUGAL_BOUNDS.north &&
      lon >= PORTUGAL_BOUNDS.west &&
      lon <= PORTUGAL_BOUNDS.east
    ) {
      setClickedPoint({ lat, lon });
    }
  }, []);

  const handleClosePopup = useCallback(() => {
    setClickedPoint(null);
  }, []);

  return (
    <div className="h-[calc(100vh-4rem)] relative">
      <MapShell tileUrl={layerConfig.url} tileAttribution={layerConfig.attribution} tileKey={mapLayer}>
        <MapClickHandler onMapClick={handleMapClick} />
        {prediction && (
          <RiskMarker lat={prediction.lat} lon={prediction.lon} risk_level={prediction.risk_level} />
        )}
      </MapShell>

      {/* Top left controls */}
      <div className="absolute top-4 left-4 z-[1000] flex flex-col gap-3">
        <LayerControl currentLayer={mapLayer} onLayerChange={setMapLayer} dark />
      </div>

      <div className="absolute bottom-8 left-4 z-[1000]">
        <RiskLegend dark />
      </div>

      {/* Info panel */}
      <div className="absolute top-4 right-4 z-[1000] w-80">
        {isLoading && (
          <div className={`${PANEL} p-4`}>
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-mist-muted">A calcular risco...</span>
            </div>
          </div>
        )}
        {error && <Alert variant="error">Erro ao carregar previsão</Alert>}
        {prediction && !isLoading && <CellPopup prediction={prediction} onClose={handleClosePopup} />}
        {!clickedPoint && !isLoading && (
          <div className={`${PANEL} p-5 text-mist`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-ember/15 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-ember" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
              <div>
                <h3 className="font-display font-bold text-mist">Clique para consultar</h3>
                <p className="text-sm text-mist-muted mt-1">
                  Clique em qualquer ponto do mapa para ver a previsão de risco de incêndio em tempo real,
                  para essa localização exata.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {clickedPoint && (
        <div className="absolute bottom-8 right-4 z-[1000] rounded-lg border border-ink-700 bg-ink-900/85 px-3 py-2 text-sm font-mono text-mist-muted shadow-2xl backdrop-blur">
          {clickedPoint.lat.toFixed(4)}, {clickedPoint.lon.toFixed(4)}
        </div>
      )}

      <ModuleBadge label="Consultar" dark />
    </div>
  );
}
